import { backendApi } from "@/services/backend/api";
import type {
  ActiveCatalogRelease,
  RefreshAllReq,
  RefreshAllRes,
  RefreshMonitorRes,
  RefreshSingleReq,
} from "@/services/backend/qpm/catalog/types/catalog";
import {
  ActivateRequest,
  ActiveCatalog,
  Catalog,
  CatalogItemRes,
  CatalogList,
  CopyCatalogItemsRequest,
  CopyCatalogItemsResponse,
  CreateCatalogRequest,
  CreateMonitorRequest,
  CreateMonitorResponse,
  DeleteCatalogRequest,
  ExcludeMonitorRequest,
  ExcludeMonitorResponse,
  GetActiveReleaseRequest,
  GetCatalogItemByBaseRequest,
  GetCatalogItemRequest,
  GetCatalogRequest,
  GetReleaseRequest,
  IncludeMonitorRequest,
  IncludeMonitorResponse,
  LatestReleaseRequest,
  ListCatalogItemsRequest,
  ListCatalogItemsResponse,
  ListMonitorsRequest,
  ListMonitorViewRes,
  ListRequest,
  PublishRequest,
  ReleaseDTO,
  ResolveActiveRequest,
  SetCatalogMonitorRentingReq,
  SetItemBasePriceReq,
  SetItemCapabilitiesReq,
  SetItemCoreFieldsReq,
  SetItemAdditionalsReq,
  SetItemConsumablesReq,
  SetItemDetailsReq,
  SetItemImageReq,
  SetItemRecommendedVisitsReq,
  SetItemIncludeInCheckpointServiceReq,
  DeleteItemImageReq,
  DeleteItemAdditionalImageReq,
  SetItemPestsReq,
  SetItemServiceTimesReq,
  SetItemRentingReq,
  SetItemAdditionalImageReq,
  SetOverridesRequest,
  UpdateDescriptionReq,
} from "@/services/backend/qpm/catalog/types/catalog";

const qpmCatalogServiceApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "QpmCatalog",
      "QpmActiveCatalog",
      "QpmActiveCatalogRelease",
      "QpmCatalogRelease",
      "QpmMonitor",
      "QpmCatalogItem",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      qpmCatalogCreate: build.mutation<Catalog, CreateCatalogRequest>({
        query: (request) => ({
          url: `/qpm/catalog`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (result) =>
          result
            ? ["QpmCatalog", { type: "QpmActiveCatalog", id: result.id }]
            : [],
      }),
      qpmCatalogGet: build.query<Catalog, GetCatalogRequest>({
        query: (request) => ({
          url: `/qpm/catalog`,
          method: "GET",
          params: request,
        }),
        providesTags: ["QpmCatalog"],
      }),
      qpmCatalogList: build.query<CatalogList, ListRequest>({
        query: (request) => ({
          url: `/qpm/catalog/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request ?? {})),
          },
        }),
        providesTags: (result) =>
          result ? [{ type: "QpmCatalog", id: "LIST" }] : [],
      }),
      qpmCatalogDelete: build.mutation<null, DeleteCatalogRequest>({
        query: (request) => ({
          url: `/qpm/catalog`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmActiveCatalog"],
      }),

      qpmCatalogCopyItems: build.mutation<
        CopyCatalogItemsResponse,
        CopyCatalogItemsRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/copy-items`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmMonitor", "QpmCatalogItem"],
      }),

      qpmCatalogDescription: build.mutation<Catalog, UpdateDescriptionReq>({
        query: (request) => ({
          url: `/qpm/catalog/description`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmActiveCatalog"],
      }),

      qpmCatalogIncludeMonitor: build.mutation<
        IncludeMonitorResponse,
        IncludeMonitorRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/include-monitor`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmMonitor", "QpmCatalogItem"],
      }),

      qpmCatalogIncludeMonitorStrict: build.mutation<
        IncludeMonitorResponse,
        IncludeMonitorRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/include-monitor-strict`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmMonitor", "QpmCatalogItem"],
      }),

      qpmCatalogExcludeMonitor: build.mutation<
        ExcludeMonitorResponse,
        ExcludeMonitorRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/exclude-monitor`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmMonitor", "QpmCatalogItem"],
      }),

      // List monitors endpoint expects base64 JSON in query param `request`.
      qpmCatalogListMonitors: build.query<
        ListMonitorViewRes,
        ListMonitorsRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/monitors`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request ?? {})),
          },
        }),
        providesTags: [{ type: "QpmMonitor", id: "LIST" }],
      }),

      // Legacy endpoints (no longer in backend) kept temporarily for call sites.
      qpmCatalogSetOverrides: build.mutation<null, SetOverridesRequest>({
        query: (request) => ({
          url: `/qpm/catalog/set-overrides`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmMonitor"],
      }),

      qpmCatalogSetMonitorRenting: build.mutation<
        null,
        SetCatalogMonitorRentingReq
      >({
        query: (request) => ({
          url: `/qpm/catalog/monitor-renting`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCatalog", "QpmMonitor"],
      }),

      qpmCatalogPublish: build.mutation<ReleaseDTO, PublishRequest>({
        query: (request) => ({
          url: `/qpm/catalog/publish`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "QpmCatalog",
          "QpmActiveCatalogRelease",
          "QpmCatalogRelease",
        ],
      }),

      qpmCatalogGetRelease: build.query<ReleaseDTO, GetReleaseRequest>({
        query: (request) => ({
          url: `/qpm/catalog/release`,
          method: "GET",
          params: request,
        }),
        providesTags: ["QpmCatalogRelease"],
      }),

      qpmCatalogGetActiveRelease: build.query<
        ActiveCatalogRelease,
        GetActiveReleaseRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/activerelease`,
          method: "GET",
          params: request,
        }),
        providesTags: ["QpmActiveCatalogRelease"],
      }),

      qpmCatalogResolveActive: build.query<
        ReleaseDTO | null,
        ResolveActiveRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/active`,
          method: "GET",
          params: request,
        }),
        providesTags: ["QpmActiveCatalog"],
      }),

      qpmCatalogActivate: build.mutation<ActiveCatalog, ActivateRequest>({
        query: (request) => ({
          url: `/qpm/catalog/activate`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmActiveCatalog", "QpmActiveCatalogRelease"],
      }),

      qpmCatalogLatestRelease: build.query<ReleaseDTO, LatestReleaseRequest>({
        query: (request) => ({
          url: `/qpm/catalog/latest-release`,
          method: "GET",
          params: request,
        }),
        providesTags: ["QpmCatalogRelease"],
      }),

      qpmCatalogCreateMonitor: build.mutation<
        CreateMonitorResponse,
        CreateMonitorRequest
      >({
        query: (body) => ({
          url: "/qpm/catalog/create-monitor",
          method: "POST",
          body,
        }),
        invalidatesTags: ["QpmCatalog", "QpmMonitor", "QpmCatalogItem"],
      }),

      // Catalog item endpoints
      qpmCatalogItemGet: build.query<CatalogItemRes, GetCatalogItemRequest>({
        query: (request) => ({
          url: `/qpm/catalog/items/get`,
          method: "GET",
          params: request,
        }),
        providesTags: [{ type: "QpmCatalogItem", id: "ITEM" }],
      }),

      qpmCatalogItemGetByBase: build.query<
        CatalogItemRes,
        GetCatalogItemByBaseRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/items/get-by-base`,
          method: "GET",
          params: request,
        }),
        providesTags: [{ type: "QpmCatalogItem", id: "ITEM" }],
      }),

      qpmCatalogItemsList: build.query<
        ListCatalogItemsResponse,
        ListCatalogItemsRequest
      >({
        query: (request) => ({
          url: `/qpm/catalog/items/list`,
          method: "GET",
          params: request,
        }),
        providesTags: [{ type: "QpmCatalogItem", id: "LIST" }],
      }),

      qpmCatalogItemSetCoreFields: build.mutation<
        CatalogItemRes,
        SetItemCoreFieldsReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/core-fields`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetCapabilities: build.mutation<
        CatalogItemRes,
        SetItemCapabilitiesReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/capabilities`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetDetails: build.mutation<
        CatalogItemRes,
        SetItemDetailsReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/details`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetPests: build.mutation<CatalogItemRes, SetItemPestsReq>({
        query: (body) => ({
          url: `/qpm/catalog/items/pests`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetImage: build.mutation<CatalogItemRes, SetItemImageReq>({
        query: (request) => {
          const body = new FormData();
          body.append("catalogItemId", request.catalogItemId);
          body.append("fileId", request.fileId);
          body.append("file", request.file || "");
          return {
            url: `/qpm/catalog/items/image`,
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetBasePrice: build.mutation<
        CatalogItemRes,
        SetItemBasePriceReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/base-price`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetRenting: build.mutation<
        CatalogItemRes,
        SetItemRentingReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/renting`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetConsumables: build.mutation<
        CatalogItemRes,
        SetItemConsumablesReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/consumables`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetAdditionals: build.mutation<
        CatalogItemRes,
        SetItemAdditionalsReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/additionals`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),
      qpmCatalogItemSetAdditionalImage: build.mutation<
        CatalogItemRes,
        SetItemAdditionalImageReq
      >({
        query: (request) => {
          const body = new FormData();
          body.append("catalogItemId", request.catalogItemId);
          body.append("additionalId", request.additionalId);
          body.append("fileId", request.fileId);
          body.append("file", request.file || "");
          return {
            url: `/qpm/catalog/items/additionals/image`,
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetRecommendedVisits: build.mutation<
        CatalogItemRes,
        SetItemRecommendedVisitsReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/recommended-visits`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetServiceTimes: build.mutation<
        CatalogItemRes,
        SetItemServiceTimesReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/service-times`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogItemSetIncludeInCheckpointService: build.mutation<
        CatalogItemRes,
        SetItemIncludeInCheckpointServiceReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/include-in-checkpoint-service`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),
      qpmCatalogItemDeleteImage: build.mutation<
        CatalogItemRes,
        DeleteItemImageReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/image`,
          method: "DELETE",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),
      qpmCatalogItemDeleteAdditionalImage: build.mutation<
        CatalogItemRes,
        DeleteItemAdditionalImageReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/items/additionals/image`,
          method: "DELETE",
          body,
        }),
        invalidatesTags: ["QpmCatalogItem", "QpmCatalog"],
      }),

      qpmCatalogReleaseRefreshMonitorPreview: build.mutation<
        RefreshMonitorRes,
        RefreshSingleReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/release/monitor/refresh/preview`,
          method: "POST",
          body,
        }),
      }),

      qpmCatalogReleaseRefreshMonitorApply: build.mutation<
        RefreshMonitorRes,
        RefreshSingleReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/release/monitor/refresh/apply`,
          method: "POST",
          body,
        }),
        invalidatesTags: [
          "QpmActiveCatalogRelease",
          "QpmCatalogRelease",
          "QpmCatalog",
          "QpmCatalogItem",
        ],
      }),

      qpmCatalogReleaseRefreshMonitorsPreview: build.mutation<
        RefreshAllRes,
        RefreshAllReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/release/monitors/refresh/preview`,
          method: "POST",
          body,
        }),
      }),

      qpmCatalogReleaseRefreshMonitorsApply: build.mutation<
        RefreshAllRes,
        RefreshAllReq
      >({
        query: (body) => ({
          url: `/qpm/catalog/release/monitors/refresh/apply`,
          method: "POST",
          body,
        }),
        invalidatesTags: [
          "QpmActiveCatalogRelease",
          "QpmCatalogRelease",
          "QpmCatalog",
          "QpmCatalogItem",
        ],
      }),
    }),
  });

export const {
  useQpmCatalogCreateMutation,
  useQpmCatalogGetQuery,
  useQpmCatalogListQuery,
  useQpmCatalogDeleteMutation,
  useQpmCatalogCopyItemsMutation,
  useQpmCatalogDescriptionMutation,
  useQpmCatalogIncludeMonitorMutation,
  useQpmCatalogIncludeMonitorStrictMutation,
  useQpmCatalogExcludeMonitorMutation,
  useQpmCatalogListMonitorsQuery,
  useQpmCatalogSetOverridesMutation,
  useQpmCatalogSetMonitorRentingMutation,
  useQpmCatalogPublishMutation,
  useQpmCatalogGetReleaseQuery,
  useQpmCatalogGetActiveReleaseQuery,
  useQpmCatalogResolveActiveQuery,
  useQpmCatalogActivateMutation,
  useQpmCatalogLatestReleaseQuery,
  useQpmCatalogCreateMonitorMutation,
  useQpmCatalogItemGetQuery,
  useQpmCatalogItemGetByBaseQuery,
  useQpmCatalogItemsListQuery,
  useQpmCatalogItemSetCoreFieldsMutation,
  useQpmCatalogItemSetCapabilitiesMutation,
  useQpmCatalogItemSetDetailsMutation,
  useQpmCatalogItemSetPestsMutation,
  useQpmCatalogItemSetImageMutation,
  useQpmCatalogItemSetBasePriceMutation,
  useQpmCatalogItemSetRentingMutation,
  useQpmCatalogItemSetConsumablesMutation,
  useQpmCatalogItemSetAdditionalsMutation,
  useQpmCatalogItemSetAdditionalImageMutation,
  useQpmCatalogItemSetRecommendedVisitsMutation,
  useQpmCatalogItemSetServiceTimesMutation,
  useQpmCatalogItemSetIncludeInCheckpointServiceMutation,
  useQpmCatalogItemDeleteImageMutation,
  useQpmCatalogItemDeleteAdditionalImageMutation,
  useQpmCatalogReleaseRefreshMonitorPreviewMutation,
  useQpmCatalogReleaseRefreshMonitorApplyMutation,
  useQpmCatalogReleaseRefreshMonitorsPreviewMutation,
  useQpmCatalogReleaseRefreshMonitorsApplyMutation,
} = qpmCatalogServiceApi;
